import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Twitter, ExternalLink, Zap, TrendingUp, Users,
  Activity, Bell, Gamepad2, BookOpen, ChevronRight,
  Shield, Radio, Webhook, Fingerprint, Rocket
} from 'lucide-react'
import { useCollectionStats } from '../hooks/useCollectionStats'

// ─── Animated counter ─────────────────────────────────────────────────────────
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    const duration = 1200
    const steps    = 40
    const step     = value / steps
    let current    = 0
    const timer = setInterval(() => {
      current += step
      if (current >= value) { setDisplay(value); clearInterval(timer) }
      else setDisplay(Math.floor(current))
    }, duration / steps)
    return () => clearInterval(timer)
  }, [value])

  return (
    <span>
      {display.toLocaleString()}{suffix}
    </span>
  )
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  label, value, suffix, icon, accent = false, delta
}: {
  label:    string
  value:    number
  suffix?:  string
  icon:     React.ReactNode
  accent?:  boolean
  delta?:   number
}) {
  return (
    <div className={`stat-card ${accent ? 'glow-border' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-pink-400/60 text-xs font-mono uppercase tracking-widest">{label}</span>
        <span className={`${accent ? 'text-pink-400' : 'text-pink-600/40'}`}>{icon}</span>
      </div>
      <div className="font-display text-2xl md:text-3xl text-white leading-none">
        <AnimatedNumber value={value} suffix={suffix} />
      </div>
      {delta !== undefined && (
        <div className={`text-xs font-mono mt-1 ${delta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Home() {
  const stats = useCollectionStats('the-10k-squad-350905768', 60_000)

  return (
    <div className="relative">
      {/* Grid bg */}
      <div className="fixed inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="fixed inset-0 bg-radial-pink pointer-events-none" />

      {/* ── HERO ── */}
      <section className="relative pt-8 pb-16 px-4">
        <div className="container mx-auto max-w-5xl">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 bg-pink-600/10 border border-pink-600/30 text-pink-300 text-xs font-mono px-4 py-2 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              LIVE · Tracking The 10K Squad on Monad
            </span>
          </div>

          {/* Title */}
          <h1 className="font-display text-center text-5xl sm:text-7xl md:text-9xl text-white tracking-widest leading-none mb-4">
            10K
            <span className="text-gradient-pink"> SQUAD</span>
            <br />
            <span className="text-3xl sm:text-5xl md:text-6xl text-pink-200/60">BOT</span>
          </h1>

          <p className="text-center text-pink-200/50 font-body text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            Real-time NFT sales tracker & community hub for{' '}
            <a
              href="https://opensea.io/collection/the-10k-squad-350905768"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-400 hover:text-pink-300 underline underline-offset-2"
            >
              The 10K Squad
            </a>{' '}
            collection on the Monad blockchain. 3,333 unique pieces. Zero compromise.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://opensea.io/collection/the-10k-squad-350905768"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink w-full sm:w-auto flex items-center justify-center gap-2 text-sm"
            >
              <ExternalLink size={14} /> View on OpenSea
            </a>
            <a
              href="https://x.com/10ksquadbot"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full sm:w-auto flex items-center justify-center gap-2 text-sm"
            >
              <Twitter size={14} /> Follow @10ksquadbot
            </a>
          </div>
        </div>
      </section>

      {/* ── LIVE STATS ── */}
      <section className="px-4 pb-16">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <h2 className="font-display text-xl text-white tracking-widest">LIVE STATS</h2>
            <span className="text-xs font-mono text-pink-400/40">— Monad Mainnet</span>
            {stats.error && (
              <span className="text-xs font-mono text-red-400/70 ml-auto">{stats.error}</span>
            )}
            {stats.loading && !stats.error && (
              <span className="text-xs font-mono text-pink-400/40 ml-auto animate-pulse">Fetching…</span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <StatCard label="Floor Price"   value={stats.floor}       suffix=" MON" icon={<TrendingUp size={14} />} accent delta={stats.floorChange} />
            <StatCard label="24H Volume"    value={stats.volume24h}   suffix=" ETH" icon={<Activity size={14} />} />
            <StatCard label="Total Volume"  value={stats.totalVolume} suffix=" ETH" icon={<Zap size={14} />} />
            <StatCard label="Top Offer"     value={stats.topOffer}    suffix=" MON" icon={<TrendingUp size={14} />} />
            <StatCard label="Listed"        value={stats.listed}      suffix=" NFTs" icon={<ExternalLink size={14} />} />
            <StatCard label="Holders"       value={stats.holders}     icon={<Users size={14} />} />
            <StatCard label="Supply"        value={stats.supply}      icon={<Shield size={14} />} />
            <StatCard label="Listed %"      value={stats.listed && stats.supply ? Math.round((stats.listed / stats.supply) * 1000) / 10 : 0} suffix="%" icon={<Activity size={14} />} />
          </div>
        </div>
      </section>

      {/* ── BOT FEATURES ── */}
      <section className="px-4 pb-16">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-mono text-pink-400/60 mb-3">
              <Radio size={12} /> POWERED BY
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-widest mb-2">
              <span className="text-gradient-pink">@10KSQUADBOT</span>
            </h2>
            <p className="text-pink-200/40 text-sm font-body max-w-md mx-auto">
              An always-on X bot tracking every sale, listing, and floor change for The 10K Squad NFT collection in real time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Real-time tracking */}
            <div className="card-glass rounded-2xl p-5 flex flex-col gap-3 group hover:glow-border transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/30 transition-colors">
                <Radio size={18} />
              </div>
              <h3 className="font-display text-lg text-white tracking-wide">Real-Time Sales</h3>
              <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                Every NFT sale from The 10K Squad collection is captured and posted to X the moment it happens — price, token ID, buyer & seller.
              </p>
              <a
                href="https://x.com/10ksquadbot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-pink-400 hover:text-pink-300 flex items-center gap-1 mt-auto transition-colors"
              >
                Follow bot <ChevronRight size={11} />
              </a>
            </div>

            {/* Discord webhook */}
            <div className="card-glass rounded-2xl p-5 flex flex-col gap-3 group hover:glow-border transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/30 transition-colors">
                <Webhook size={18} />
              </div>
              <h3 className="font-display text-lg text-white tracking-wide">Discord Webhooks</h3>
              <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                Sales are also pushed to community Discord servers via webhook — instant notifications so your community never misses a floor-mover.
              </p>
              <span className="text-xs font-mono text-pink-600/40 mt-auto">Configurable per server</span>
            </div>

            {/* Floor alerts */}
            <div className="card-glass rounded-2xl p-5 flex flex-col gap-3 group hover:glow-border transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/30 transition-colors">
                <Bell size={18} />
              </div>
              <h3 className="font-display text-lg text-white tracking-wide">Floor Alerts</h3>
              <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                Automatic alerts whenever the floor price moves significantly — so holders and buyers stay informed without watching charts all day.
              </p>
              <span className="text-xs font-mono text-pink-600/40 mt-auto">
                {stats.floorChange !== 0
                  ? `${stats.floorChange >= 0 ? '+' : ''}${stats.floorChange.toFixed(1)}% in 24h`
                  : '+50.8% in 24h'}
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 card-glass rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 glow-border-strong">
            <div>
              <div className="text-xs font-mono text-pink-400/60 mb-1">THE TRACKER IS LIVE</div>
              <p className="text-white font-body font-semibold">
                Follow <span className="text-pink-400">@10ksquadbot</span> on X for live sale alerts
              </p>
            </div>
            <a
              href="https://x.com/10ksquadbot"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink flex items-center gap-2 whitespace-nowrap"
            >
              <Twitter size={14} /> Follow Now
            </a>
          </div>
        </div>
      </section>

      {/* ── EXPLORE SECTIONS ── */}
      <section className="px-4 pb-20">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-display text-3xl md:text-4xl text-center text-white tracking-widest mb-8">
            EXPLORE THE <span className="text-gradient-pink">SQUAD</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Games */}
            <Link
              to="/games"
              className="group card-glass rounded-2xl p-6 flex flex-col gap-4 hover:glow-border transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/40 transition-colors">
                  <Gamepad2 size={22} />
                </div>
                <ChevronRight size={18} className="text-pink-600/40 group-hover:text-pink-400 group-hover:translate-x-1 transition-all" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-white tracking-wide mb-1">GAMES</h3>
                <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                  Community mini-games built for Squad holders. Compete, earn bragging rights, and have fun in the 10K universe.
                </p>
              </div>
              <div className="text-xs font-mono text-pink-400/40 border-t border-pink-900/30 pt-3">
                → Coming soon · Design in progress
              </div>
            </Link>

            {/* Facts */}
            <Link
              to="/facts"
              className="group card-glass rounded-2xl p-6 flex flex-col gap-4 hover:glow-border transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/40 transition-colors">
                  <BookOpen size={22} />
                </div>
                <ChevronRight size={18} className="text-pink-600/40 group-hover:text-pink-400 group-hover:translate-x-1 transition-all" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-white tracking-wide mb-1">10K FACTS</h3>
                <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                  Lore, trivia, rarity insights, and fun facts about The 10K Squad collection — everything you need to know about your Squad.
                </p>
              </div>
              <div className="text-xs font-mono text-pink-400/40 border-t border-pink-900/30 pt-3">
                → Coming soon · Design in progress
              </div>
            </Link>

            {/* Identity */}
            <Link
              to="/identity"
              className="group card-glass rounded-2xl p-6 flex flex-col gap-4 hover:glow-border transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/40 transition-colors">
                  <Fingerprint size={22} />
                </div>
                <ChevronRight size={18} className="text-pink-600/40 group-hover:text-pink-400 group-hover:translate-x-1 transition-all" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-white tracking-wide mb-1">IDENTITY</h3>
                <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                  Claim your permanent <span className="text-pink-400 font-mono">handle.10ksquad</span> on-chain identity. Exclusive to Squad holders. Soulbound, non-transferable.
                </p>
              </div>
              <div className="text-xs font-mono text-pink-400/40 border-t border-pink-900/30 pt-3">
                → Holders only · Mint your name
              </div>
            </Link>

            {/* Launcher */}
            <Link
              to="/tokens"
              className="group card-glass rounded-2xl p-6 flex flex-col gap-4 hover:glow-border transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-pink-600/20 border border-pink-600/30 flex items-center justify-center text-pink-400 group-hover:bg-pink-600/40 transition-colors">
                  <Rocket size={22} />
                </div>
                <ChevronRight size={18} className="text-pink-600/40 group-hover:text-pink-400 group-hover:translate-x-1 transition-all" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-white tracking-wide mb-1">LAUNCH</h3>
                <p className="text-pink-200/50 text-sm font-body leading-relaxed">
                  Launch your own memecoin on Monad. Exclusively for{' '}
                  <span className="text-pink-400 font-mono">.10ksquad</span> domain holders. Bonding curve included.
                </p>
              </div>
              <div className="text-xs font-mono text-pink-400/40 border-t border-pink-900/30 pt-3">
                → Holders only · Squad token launchpad
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}